import { useMemo, useState, useEffect, useContext } from 'react';
import { Fade, Paper, Stack, Typography, useTheme, Checkbox, Button, Tooltip } from '@mui/material';
import { DisplayTable, errFeedbackFn, roomDetailClasses } from '../../../utility';
import CreatePlantPopup from '../Popup/CreatePlantPopup';
import APIService from '../../../../api/apiService';
import { EditIcon2, IconHistory, MetrcLogo, RefreshIcon } from '../../../utility/CustomSvgIcons';
import { DelIcon2 } from '../../../utility/CustomSvgIcons';
import { IconButton } from '@mui/material';
import _PlantHistoryPopup from './_PlantHistoryPopup';
import { FRTLContext } from '../../../../context/FRTLContext';
import { fetchFrtlData } from '../../../../api/apiUtils';
import MetrcPlantEditPopup from '../../Popup/MetrcPlantEditPopup';
import { useParams } from 'react-router';
import { FeedbackContext } from '../../../../App';
import { getFrtlAliasString } from '../../../../utility/frtlUtils';
import { plantKeyTitleMap } from '../../utils/TableTitles';
import LoadingSpinner from '../../../utility/LoadingSpinner';

const _PlantsTab = (props) => {
  const { plants = [], currDarkThemeClass = '', cropPlanPerms, values: parentValues, handleSetValues, handleErrorWithFeedback } = props;
  
  const [detailChildren, setDetailChildren] = useState({
    open: false,
    onDelete: null,
    onClose: null,
  });

  const plantTableTitles = useMemo(
    () =>
      plantKeyTitleMap.map(e => ({
        title:
          e.key === 'metrc_plant_id' ? (
            <Tooltip title="Metrc Synced" arrow placement="top">
              <span style={{ cursor: 'help' }}>{e.title}</span>
            </Tooltip>
          ) : (
            e.title
          ),
      })),
    []
  );

  const { cropplanid } = useParams();
  const feedbackCX = useContext(FeedbackContext);

  const [openEditPlantPopup, setOpenEditPlantPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [values, setValues] = useState({
    plantDetails: [],
    selectedRows: [],
    selectAll: false,
    openCreatePlant: false,
    isEditMode: false,
    currentPlant: null,
    openHistoryPopup: false,
    plantHistory: [],
    selectedAction: null,
    dataInitialized: false,
  });

  const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);
  const theme = useTheme();

  const fetchPlants = async () => {
    setIsLoading(true);
    try {
      const response = await APIService.fetchInstance(`cropplan-plants/?cropplan_id=${cropplanid}`);
      if (response.status === 200) {
        setValues(prevState => ({
          ...prevState,
          plantDetails: response.data || [],
          dataInitialized: true,
        }));
        if (handleSetValues) {
          handleSetValues('plants', response.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching plants:', error);
      handleErrorWithFeedback(errFeedbackFn({}))(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!values.dataInitialized || plants.length === 0) {
      fetchPlants();
    }
  }, []);

  useEffect(() => {
    if (values.dataInitialized && plants.length > 0 && JSON.stringify(plants) !== JSON.stringify(values.plantDetails)) {
      console.log('Updating plants from props due to external changes');
      setValues(prevState => ({
        ...prevState,
        plantDetails: plants,
      }));
    }
  }, [plants, values.dataInitialized, values.plantDetails]);

  useEffect(() => {
    if (!frtlContextValue) {
      fetchFrtlData()
        .then(data => {
          setFrtlContextValue(data || {});
        })
        .catch(error => {
          console.error('Error loading FRTL data:', error);
          setFrtlContextValue({});
        });
    }
  }, [frtlContextValue, setFrtlContextValue]);

  const filteredPlants = useMemo(() => {
    if (!searchTerm) return values.plantDetails;

    return values.plantDetails.filter(plant => {
      const searchLower = searchTerm.toLowerCase();
      return (
        plant.batch_name?.toLowerCase().includes(searchLower) ||
        plant.strain_name?.toLowerCase().includes(searchLower) ||
        plant.metrc_tag_number?.toLowerCase().includes(searchLower) ||
        plant.plant_phase?.toLowerCase().includes(searchLower)
        // plant.frtl?.toLowerCase().includes(searchLower)
      );
    });
  }, [values.plantDetails, searchTerm]);

  const handlePlantAddRes = res => {
    const { data } = res;
    const updatedPlants = [...values.plantDetails, data];

    setValues(prevState => ({
      ...prevState,
      plantDetails: updatedPlants,
      openCreatePlant: false,
    }));

    handleSetValues('plants', updatedPlants);
  };

  const handleEditPlantRes = res => {
    const { data } = res;
    const updatedPlants = values.plantDetails.map(plant => (plant.id === data.id ? data : plant));

    setValues(prevState => ({
      ...prevState,
      plantDetails: updatedPlants,
      openCreatePlant: false,
      isEditMode: false,
      currentPlant: null,
    }));

    handleSetValues('plants', updatedPlants);
  };

  const handleDeletePlantRes = () => {
    const updatedPlants = values.plantDetails.filter(plant => !values.selectedRows.includes(plant.id));

    setValues(prevState => ({
      ...prevState,
      plantDetails: updatedPlants,
      selectedRows: [],
      selectAll: false,
    }));
    handleSetValues('plants', updatedPlants);
  };

  const handleHistoryIconClick = plant => {
    setValues(state => ({
      ...state,
      openHistoryPopup: true,
      plantHistory: plant.plant_phase_history || [],
      currentMetricTag: plant.metrc_tag_number,
    }));
  };

  const handleCloseHistoryPopup = () => {
    setValues(state => ({
      ...state,
      openHistoryPopup: false,
      plantHistory: [],
    }));
  };

  const handleAddPlant = payload => {
    const entity = `cropplan-plants/`;
    if (values.isEditMode) {
      handleEditPlant(payload);
    } else {
      APIService.createInstance(entity, payload)
        .then(handlePlantAddRes)
        .catch(handleErrorWithFeedback(errFeedbackFn({})));
    }
  };

  const handleEditPlant = payload => {
    const entity = `cropplan-plants/${payload.id}/`;
    APIService.modifyInstance(entity, payload)
      .then(handleEditPlantRes)
      .catch(handleErrorWithFeedback(errFeedbackFn({})));
  };

  const handleDeletePlant = (plantId) => () => {

    const errConfig = {
      404: 'Plant not found',
      400: 'Plant could not be deleted',
    };
  
    APIService.deleteInstance(`cropplan-plants/${plantId}/`)
      .then(() => {
  
        const updatedPlants = values.plantDetails.filter(p => p.id !== plantId);
  
        setValues(prev => ({
          ...prev,
          plantDetails: updatedPlants,
        }));
  
        handleSetValues('plants', updatedPlants);
  
        handlePopupClose();
      })
      .catch(handleErrorWithFeedback(errFeedbackFn(errConfig)));
  };

  const handleAddPlantDetails = () => {
    setValues(state => ({
      ...state,
      openCreatePlant: true,
      isEditMode: false,
      currentPlant: null,
    }));
  };

  const handleEditPlantDetails = plant => {
    if(!cropPlanPerms?.update){
          feedbackCX.setContextValue(true, {
              type: 'success',
              message: "You don't have permissions to Update Data",
          });
          return
    }
    setValues(state => ({
      ...state,
      openCreatePlant: true,
      isEditMode: true,
      currentPlant: plant,
    }));
  };

  const handlePopupClose = () => {
    setDetailChildren(prev => ({
      ...prev,
      open: false,
    }));
  };

  const handleDeletePlantPopup = (plant) => () => {

  if (plant.metrc_plant_id) {
    feedbackCX.setContextValue(true, {
      type: 'error',
      message: "Metrc synced plants cannot be deleted",
    });
    return;
  }

    if (!cropPlanPerms?.update) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: "You don't have permissions to delete data",
      });
      return;
    }
  
    setDetailChildren({
      open: true,
      onClose: handlePopupClose,
      onDelete: handleDeletePlant(plant.id),
      currDarkThemeClass,
    });
  };


  const handleDestroyPlants = () => {
    const { plantDetails, selectedRows } = values;
    if (selectedRows.length > 0) {
      const promises = selectedRows.map(id => APIService.deleteInstance(`cropplan-plants/${id}/`));
      Promise.all(promises)
        .then(() => {
          const updatedPlants = plantDetails.filter(plant => !selectedRows.includes(plant.id));

          setValues(prevState => ({
            ...prevState,
            plantDetails: updatedPlants,
            selectedRows: [],
            selectAll: false,
          }));
          handleSetValues('plants', updatedPlants);
        })
        .catch(handleErrorWithFeedback(errFeedbackFn({})));
    }
  };

  const handleMetrcEditSave = response => {
    fetchPlants();
  };

  const refreshPlants = () => {
    fetchPlants();
    feedbackCX.setContextValue(true, {
      type: 'success',
      message: 'Refreshed Plants data Successfully.',
    });
  };

  const refreshIconProps = {
    style: { width: '20px', height: '20px', marginRight: '4px', color: theme?.palette?.primary?.['400'] },
  };

  const actionBtnsList = [
    {
      key: 'Change Location',
      isMetrc: true,
      disabled: !cropPlanPerms?.update || values.selectedRows.length === 0,
      onClick: () => {
        if (values.selectedRows.length === 0) {
          console.warn('No plants selected for location change');
          return;
        }
        setValues(prevState => ({
          ...prevState,
          selectedAction: 'change_location',
        }));
        setOpenEditPlantPopup(true);
      },
    },
    {
      key: 'Change Growth Phase',
      isMetrc: true,
      disabled: !cropPlanPerms?.update || values.selectedRows.length === 0,
      onClick: () => {
        if (values.selectedRows.length === 0) {
          console.warn('No plants selected for growth phase change');
          return;
        }
        setValues(prevState => ({
          ...prevState,
          selectedAction: 'change_growth_phase',
        }));
        setOpenEditPlantPopup(true);
      },
    },
    {
      key: 'Destroy',
      isMetrc: true,
      disabled: !cropPlanPerms?.update || values.selectedRows.length === 0,
      onClick: () => {
        if (values.selectedRows.length === 0) {
          console.warn('No plants selected for destruction');
          return;
        }
        setValues(prevState => ({
          ...prevState,
          selectedAction: 'destroy_plants',
        }));
        setOpenEditPlantPopup(true);
      },
    },
    {
      key: 'Create Plant',
      isMetrc: false,
      disabled: !cropPlanPerms?.update,
      onClick: handleAddPlantDetails,
    },
    {
      key: 'Refresh',
      isMetrc: false,
      disabled: !cropPlanPerms?.update || isLoading,
      onClick: refreshPlants,
    },
  ].map((e, i) => (
    <Button
      key={i}
      style={{
        color: e.disabled ? theme?.palette?.grey?.['500'] : theme?.palette?.primary?.['400'],
        opacity: e.disabled ? 0.6 : 1,
      }}
      disabled={e.disabled}
      onClick={e.onClick}
    >
      {e.isMetrc && <MetrcLogo />}
      {e.key === 'Refresh' ? (
        <>
          <RefreshIcon {...refreshIconProps} />
          {e.key}
        </>
      ) : (
        e.key
      )}
    </Button>
  ));

  const handleCheckboxChange = id => {
    setValues(prevData => {
      const { selectedRows } = prevData;
      const updatedSelectedRows = selectedRows.includes(id) ? selectedRows.filter(selectedId => selectedId !== id) : [...selectedRows, id];
      return {
        ...prevData,
        selectedRows: updatedSelectedRows,
        selectAll: false,
      };
    });
  };

  const handleSelectAllChange = () => {
    setValues(prevData => {
      const { selectAll } = prevData;
      const updatedSelectAll = !selectAll;
      const updatedSelectedRows = updatedSelectAll ? filteredPlants.map(plant => plant.id) : [];
      return {
        ...prevData,
        selectAll: updatedSelectAll,
        selectedRows: updatedSelectedRows,
      };
    });
  };

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
    setValues(prevState => ({
      ...prevState,
      selectedRows: [],
      selectAll: false,
    }));
  };

  const plantSubTypoProps = {
    className: `${roomDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`,
  };

  const detailplantsPaperProps = {
    className: `${roomDetailClasses?.detailLightsPaper} ${currDarkThemeClass} roomDetailTabsContent`,
    square: true,
    variant: 'none',
  };
  
  const plantEntries = filteredPlants.map((val, idx) => [
    <Checkbox
      key={`checkbox-${idx}`}
      checked={values.selectAll || values.selectedRows.includes(val.id)}
      onChange={() => handleCheckboxChange(val.id)}
      style={{ color: theme?.palette?.grey?.['400'] }}
    />,
    `${idx + 1}`.padStart(2, '0'),
    ...plantKeyTitleMap.map(e => {
      if (e.key === 'frtl') {
        return getFrtlAliasString(val[e.key], frtlContextValue) || '-';
      } else if (e.key === 'metrc_plant_id') {
        return val[e.key] ? <MetrcLogo /> : '-';
      } else {
        return val[e.key] || '-';
      }
    }),
    <Stack direction="row" spacing={1} key={`actions-${idx}`}>
      <IconButton onClick={() => handleEditPlantDetails(val)}>
        <EditIcon2 style={{ color: 'inherit' }} />
      </IconButton>
      <IconButton onClick={() => handleHistoryIconClick(val)}>
        <IconHistory style={{ color: 'grey' }} />
      </IconButton>
      {!  val.metrc_plant_id && 
      <IconButton onClick={handleDeletePlantPopup(val)}>
        <DelIcon2 style={{ color: 'inherit' }} />
      </IconButton>
      }
    </Stack>,
  ]);

  const plantsTableValues = {
    entity: 'Plants',
    tableEntries: plantEntries,
    toShowAddBtn: false,
    tableTitles: [
      {
        title: (
          <Checkbox
            checked={values.selectAll}
            onChange={handleSelectAllChange}
            style={{ color: theme?.palette?.grey?.['400'] }}
            indeterminate={values.selectedRows.length > 0 && values.selectedRows.length < filteredPlants.length}
          />
        ),
      },
      { title: '#' },
      ...plantTableTitles,
      { title: 'Actions' },
    ],
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    isLoading,
  };

  const pActionStackProps = {
    direction: 'row',
    spacing: 2,
    justifyContent: 'flex-end',
    alignItems: 'center',
  };

  const pTPlantPopupProps = {
    open: values?.openCreatePlant,
    currDarkThemeClass,
    parentValues,
    cropPlanPerms,
    handleSetValues,
    handleAddPlant,
    isEditMode: values?.isEditMode,
    currentPlant: values?.currentPlant,
    onClose: () =>
      setValues(state => ({
        ...state,
        openCreatePlant: false,
        isEditMode: false,
        currentPlant: null,
      })),
  };

  const historyPopupProps = {
    open: values.openHistoryPopup,
    onClose: handleCloseHistoryPopup,
    history: values.plantHistory,
    currDarkThemeClass: currDarkThemeClass,
    metrcTagNumber: values.currentMetricTag,
  };

  const handleMetrcEditPlantPopupClose = () => {
    setOpenEditPlantPopup(false);
    setValues(prevState => ({
      ...prevState,
      selectedAction: null,
    }));
  };

  const metrcPlantEditPopupProps = {
    open: openEditPlantPopup,
    selectedFarmId: parentValues?.farm,
    onClose: handleMetrcEditPlantPopupClose,
    onSave: handleMetrcEditSave,
    cropPlanPerms,
    currDarkThemeClass,
    selectedAction: values.selectedAction,
    selectedRows: values.selectedRows,
    selectedPlants: values.plantDetails.filter(plant => values.selectedRows.includes(plant.id)),
    allPlants: values.plantDetails,
  };

  return (
    <>
    <Fade in={true} timeout={1000}>
      <Paper {...detailplantsPaperProps}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" paddingY={1} width="100%">
          <Typography {...plantSubTypoProps}>
            {`Plants (${filteredPlants?.length ?? 0}${searchTerm ? ` of ${values.plantDetails?.length ?? 0}` : ''})`}
            {values.selectedRows.length > 0 && (
              <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>- {values.selectedRows.length} selected</span>
            )}
          </Typography>
          {/* <OutlinedInput {...outlinedInputProps} /> */}
          <Stack {...pActionStackProps}>{actionBtnsList}</Stack>
        </Stack>
        {isLoading ? (
          <LoadingSpinner currDarkThemeClass={currDarkThemeClass} message="Loading Plants Data" />
        ) : (
          <DisplayTable {...plantsTableValues} />
        )}
        <CreatePlantPopup {...pTPlantPopupProps} />
        <_PlantHistoryPopup {...historyPopupProps} />
        {openEditPlantPopup && <MetrcPlantEditPopup {...metrcPlantEditPopupProps} />}
      </Paper>
    </Fade>
    {detailChildren.open && (
      <Fade in={detailChildren.open}>
        <Paper
          style={{
            position: "fixed",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "24px",
            zIndex: 1300,
          }}
          className={currDarkThemeClass}
        >
          <Stack spacing={2}>
            <Typography variant="h6">
              Delete Plant
            </Typography>
    
            <Typography>
              Are you sure you want to delete this plant?
            </Typography>
    
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={detailChildren.onClose}>
                Cancel
              </Button>
    
              <Button
                color="error"
                variant="contained"
                onClick={detailChildren.onDelete}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Fade>
    )}
  </>
  );
};

export default _PlantsTab;
